import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  AdminPanelSettingsOutlined,
  ApartmentOutlined,
  Brightness4Outlined,
  Brightness7Outlined,
  Check,
  CreateOutlined,
  DashboardCustomizeOutlined,
  DescriptionOutlined,
  FeedbackOutlined,
  FolderOutlined,
  HomeOutlined,
  InsightsOutlined,
  Logout,
  MicNoneOutlined,
  NewReleasesOutlined,
  PersonOutline,
  SettingsOutlined,
  UnfoldMore,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useApp } from '../../context/AppContext';
import { customerPath, flushUnsavedWork, goToRecordMeeting, requestQuickCreate } from '../../utils/navigation';
import { WHATS_NEW_LINK_PROPS } from '../WhatsNewPage';
import { CONSOLE_PATH, usePlatformAdminAccess } from '../console/platformAdminApi';

/** Max customers listed in the sidebar section. */
const SIDEBAR_CUSTOMER_LIMIT = 5;
/** Minimum gap between unlinked-recording refreshes triggered by focus. */
const UNLINKED_REFRESH_MS = 60000;

/**
 * Whether a nav item is active for the current path.
 * @param {string} pathname - location.pathname.
 * @param {{match: (p: string) => boolean}} item - Nav item.
 * @returns {boolean} Active.
 */
const isActive = (pathname, item) => item.match(pathname);

/**
 * Count of recordings (Fathom/Recall) not yet linked to a project, for the Recordings badge.
 * @param {string|undefined} accountId - Active account id.
 * @param {Function} getUnlinkedSessions - AppContext action.
 * @returns {number} Count.
 */
function useUnlinkedRecordingCount(accountId, getUnlinkedSessions) {
  const [count, setCount] = useState(0);
  const lastRef = useRef(0);
  const refresh = useCallback(async () => {
    if (!accountId || typeof getUnlinkedSessions !== 'function') {
      setCount(0);
      return;
    }
    lastRef.current = Date.now();
    const result = await getUnlinkedSessions(accountId);
    if (result?.success) setCount(result.data.length);
  }, [accountId, getUnlinkedSessions]);

  useEffect(() => {
    refresh();
    const onChanged = () => refresh();
    const onFocus = () => {
      if (Date.now() - lastRef.current > UNLINKED_REFRESH_MS) refresh();
    };
    window.addEventListener('quillai:data-changed', onChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('quillai:data-changed', onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);
  return count;
}

/**
 * One sidebar nav row (full row or icon-only rail button).
 * @param {object} props - Props.
 * @returns {JSX.Element} Row.
 */
function NavRow({ item, active, collapsed, onNavigate }) {
  const icon = item.badge > 0 && collapsed ? (
    <Badge color="error" variant="dot" overlap="circular">
      {item.icon}
    </Badge>
  ) : item.icon;
  const row = (
    <ListItemButton
      component={RouterLink}
      to={item.to}
      selected={active}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      onClick={onNavigate}
      sx={{
        mb: 0.25,
        ...(collapsed ? { justifyContent: 'center', px: '0 !important', minWidth: 0, width: 44, mx: 'auto' } : {}),
      }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, color: active ? 'inherit' : 'var(--q-muted, #5E5A54)', '& svg': { fontSize: 20 } }}>
        {icon}
      </ListItemIcon>
      {!collapsed && <ListItemText primary={item.label} />}
      {!collapsed && item.badge > 0 && (
        <Box
          component="span"
          aria-label={`${item.badge} not linked to a project`}
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: 'var(--q-radius-pill, 999px)',
            bgcolor: 'var(--q-danger-soft, #FCE8E2)',
            color: 'var(--q-danger-text, #9C2C14)',
            fontSize: 11.5,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.badge > 99 ? '99+' : item.badge}
        </Box>
      )}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip title={item.badge ? `${item.label} (${item.badge} to link)` : item.label} placement="right">
      {row}
    </Tooltip>
  ) : row;
}

/**
 * Left navigation: brand + workspace switcher, primary "New" action, core objects, a short
 * customer list and the account row (profile menu, theme toggle). Renders as a full sidebar,
 * an icon rail (collapsed) or the content of the mobile drawer.
 * @param {object} props - Props.
 * @param {boolean} props.collapsed - Icon rail mode.
 * @param {boolean} props.inDrawer - Rendered inside the mobile drawer.
 * @param {boolean} props.isDarkMode - Current theme.
 * @param {() => void} props.toggleTheme - Theme toggle.
 * @param {() => void} [props.onOpenFeedback] - Open the feedback dialog.
 * @param {() => void} [props.onNavigate] - Called after a navigation (closes the drawer).
 * @returns {JSX.Element} Sidebar.
 */
export default function AppSidebar({ collapsed = false, inDrawer = false, isDarkMode, toggleTheme, onOpenFeedback, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state: authState, actions: authActions } = useAuth();
  const { state: appState, actions: appActions } = useApp();
  const [newMenuAnchor, setNewMenuAnchor] = useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const accountId = authState.activeAccount?.id;
  const unlinked = useUnlinkedRecordingCount(accountId, appActions.getUnlinkedSessions);
  const canManageTemplates = authActions.canManageTemplates();
  const isAdmin = authActions.isAccountAdmin();
  const isPlatformAdmin = usePlatformAdminAccess();
  const memberships = (authState.memberships || []).filter((m) => m.accounts);
  const username = authState.user?.username || authState.user?.full_name || authState.user?.email || 'You';

  const navItems = useMemo(() => {
    const items = [
      { key: 'home', label: 'Home', to: '/home', icon: <HomeOutlined />, match: (p) => p === '/home' },
      { key: 'recordings', label: 'Recordings', to: '/customers/sessions', icon: <MicNoneOutlined />, badge: unlinked, match: (p) => p.startsWith('/customers/sessions') },
      { key: 'customers', label: 'Customers', to: '/customers', icon: <ApartmentOutlined />, match: (p) => p.startsWith('/customers') && !p.startsWith('/customers/sessions') },
      { key: 'projects', label: 'Projects', to: '/projects', icon: <FolderOutlined />, match: (p) => p.startsWith('/projects') },
    ];
    if (canManageTemplates) {
      items.push({ key: 'templates', label: 'Templates', to: '/templates', icon: <DashboardCustomizeOutlined />, match: (p) => p.startsWith('/templates') });
    }
    items.push({ key: 'documents', label: 'Documents', to: '/documents', icon: <DescriptionOutlined />, match: (p) => p.startsWith('/documents') || p === '/generate' || p === '/editor' });
    return items;
  }, [unlinked, canManageTemplates]);

  const topCustomers = useMemo(() => {
    const projectCounts = new Map();
    (appState.projects || []).forEach((p) => {
      if (p.customer_id) projectCounts.set(p.customer_id, (projectCounts.get(p.customer_id) || 0) + 1);
    });
    return [...(appState.customers || [])]
      .map((c) => ({ ...c, projectCount: projectCounts.get(c.id) || 0 }))
      .sort((a, b) => b.projectCount - a.projectCount || String(a.name).localeCompare(String(b.name)))
      .slice(0, SIDEBAR_CUSTOMER_LIMIT);
  }, [appState.customers, appState.projects]);

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const newActions = [
    { key: 'record', label: 'Record a meeting', icon: <MicNoneOutlined fontSize="small" />, run: () => { goToRecordMeeting(navigate); onNavigate?.(); } },
    { key: 'document', label: 'Generate a document', icon: <CreateOutlined fontSize="small" />, run: () => go('/generate') },
    { key: 'project', label: 'New project', icon: <FolderOutlined fontSize="small" />, run: () => { onNavigate?.(); requestQuickCreate('project'); } },
    { key: 'customer', label: 'New customer', icon: <ApartmentOutlined fontSize="small" />, run: () => { onNavigate?.(); requestQuickCreate('customer'); } },
    // Template creation goes through the plan's template limit check (QuickCreateHost).
    ...(canManageTemplates ? [{ key: 'template', label: 'New template', icon: <DashboardCustomizeOutlined fontSize="small" />, run: () => { onNavigate?.(); requestQuickCreate('template'); } }] : []),
  ];

  const openSettings = () => window.dispatchEvent(new Event('quillai:open-settings'));
  /**
   * Log out. Signing out unmounts the open page without a router navigation (the shell goes
   * away), so pending edits are saved first rather than left to the route blocker.
   * @returns {Promise<void>}
   */
  const handleLogout = async () => {
    setUserMenuAnchor(null);
    const saved = await flushUnsavedWork();
    if (!saved && !window.confirm('Some changes could not be saved. Log out anyway?')) return;
    onNavigate?.();
    await authActions.logout();
    navigate('/login');
  };

  const initial = String(username).charAt(0).toUpperCase() || 'U';
  const accountName = authState.activeAccount?.name || 'Workspace';

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: collapsed ? 1 : 1.5,
        pt: 2,
        pb: 1.5,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        width: collapsed ? 64 : inDrawer ? '100%' : 248,
        boxSizing: 'border-box',
      }}
    >
      {/* Brand + workspace */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: collapsed ? 0 : 1, pb: 2, justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <Box component="img" src="/QuillAI-Logo.png" alt="Quill AI" sx={{ height: 32, width: 'auto', flex: 'none', display: 'block' }} />
        {!collapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, flex: 1, minWidth: 0 }}>
            <Typography component="span" sx={{ fontWeight: 650, fontSize: 15, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              Quill AI
            </Typography>
            <Typography component="span" noWrap title={accountName} sx={{ fontSize: 12, color: 'var(--q-muted-2, #6B665F)', lineHeight: 1.3 }}>
              {accountName}
            </Typography>
          </Box>
        )}
        {!collapsed && memberships.length > 1 && (
          <Tooltip title="Switch workspace">
            <IconButton
              size="small"
              aria-label="Switch workspace"
              aria-haspopup="menu"
              onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
              sx={{ width: 28, height: 28, color: 'var(--q-muted-2, #6B665F)' }}
            >
              <UnfoldMore fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Menu anchorEl={accountMenuAnchor} open={Boolean(accountMenuAnchor)} onClose={() => setAccountMenuAnchor(null)} slotProps={{ paper: { sx: { minWidth: 220 } } }}>
        <Typography component="li" variant="caption" sx={{ display: 'block', px: 2, py: 0.5, fontWeight: 600, color: 'var(--q-muted, #5E5A54)' }}>
          Workspaces
        </Typography>
        {memberships.map((membership) => {
          const account = membership.accounts;
          const selected = account?.id === accountId;
          return (
            <MenuItem
              key={membership.account_id}
              selected={selected}
              onClick={async () => {
                setAccountMenuAnchor(null);
                if (!selected) await authActions.switchAccount(membership.account_id);
              }}
            >
              <ListItemText primary={account?.name || 'Workspace'} secondary={membership.plan_name ? `${membership.plan_name} plan` : undefined} />
              {selected && <Check fontSize="small" sx={{ ml: 1, color: 'var(--q-accent, #4F3BD6)' }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Primary action */}
      {collapsed ? (
        <Tooltip title="New" placement="right">
          <IconButton
            aria-label="New"
            aria-haspopup="menu"
            onClick={(event) => setNewMenuAnchor(event.currentTarget)}
            sx={{ mx: 'auto', mb: 1.5, width: 44, height: 44, borderRadius: '14px', bgcolor: 'var(--q-surface, #FFFFFF)', boxShadow: 'var(--q-shadow-md)', color: 'var(--q-accent, #4F3BD6)', '&:hover': { bgcolor: 'var(--q-surface, #FFFFFF)' } }}
          >
            <Add />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          aria-haspopup="menu"
          onClick={(event) => setNewMenuAnchor(event.currentTarget)}
          startIcon={<Add sx={{ color: 'var(--q-accent, #4F3BD6)', fontSize: '22px !important' }} />}
          sx={{
            alignSelf: 'flex-start',
            height: 48,
            pl: 2,
            pr: 2.5,
            mx: 0.5,
            mb: 1.75,
            borderRadius: '14px',
            bgcolor: 'var(--q-surface, #FFFFFF)',
            color: 'var(--q-text, #1C1B19)',
            fontSize: 14,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'var(--q-shadow-md)',
            '&:hover': { bgcolor: 'var(--q-surface, #FFFFFF)', boxShadow: 'var(--q-shadow-lg)' },
          }}
        >
          New
        </Button>
      )}
      <Menu anchorEl={newMenuAnchor} open={Boolean(newMenuAnchor)} onClose={() => setNewMenuAnchor(null)} slotProps={{ paper: { sx: { minWidth: 220 } } }}>
        {newActions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              setNewMenuAnchor(null);
              action.run();
            }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Core objects */}
      <List disablePadding>
        {navItems.map((item) => (
          <NavRow key={item.key} item={item} active={isActive(pathname, item)} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </List>

      {/* Customers */}
      {!collapsed && topCustomers.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 2.5, pb: 0.75 }}>
            <Typography component="h2" sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--q-faint, #716C65)' }}>
              Customers
            </Typography>
            <Tooltip title="New customer">
              <IconButton size="small" aria-label="New customer" onClick={() => { onNavigate?.(); requestQuickCreate('customer'); }} sx={{ width: 24, height: 24, color: 'var(--q-muted-2, #6B665F)' }}>
                <Add sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <List disablePadding aria-label="Customers">
            {topCustomers.map((customer) => {
              const to = customerPath(customer.id);
              const active = pathname === to;
              return (
                <ListItemButton
                  key={customer.id}
                  component={RouterLink}
                  to={to}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  onClick={onNavigate}
                  sx={{ minHeight: '32px !important', mb: 0.25 }}
                >
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{ width: 20, height: 20, mr: 1.25, flex: 'none', borderRadius: '5px', bgcolor: 'var(--q-subtle, #E8E5DF)', color: 'var(--q-text-3, #4A463F)', fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {String(customer.name || '?').charAt(0).toUpperCase()}
                  </Box>
                  <ListItemText primary={customer.name} primaryTypographyProps={{ noWrap: true }} sx={{ '& .MuiListItemText-primary': { fontSize: '13.5px !important', fontWeight: '400 !important' } }} />
                  <Typography component="span" sx={{ fontSize: 12, color: 'var(--q-faint, #716C65)', fontVariantNumeric: 'tabular-nums', ml: 1 }} aria-label={`${customer.projectCount} projects`}>
                    {customer.projectCount}
                  </Typography>
                </ListItemButton>
              );
            })}
            <ListItemButton component={RouterLink} to="/customers" onClick={onNavigate} sx={{ minHeight: '32px !important' }}>
              <ListItemText primary={`All ${(appState.customers || []).length} customers`} sx={{ '& .MuiListItemText-primary': { fontSize: '13px !important', color: 'var(--q-accent, #4F3BD6)' } }} />
            </ListItemButton>
          </List>
        </>
      )}

      {/* Footer */}
      <Box sx={{ mt: 'auto', pt: 2.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {isAdmin && (
          <List disablePadding>
            <NavRow
              item={{ key: 'admin', label: 'Admin', to: '/admin', icon: <AdminPanelSettingsOutlined />, match: (p) => p.startsWith('/admin') }}
              active={pathname.startsWith('/admin')}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </List>
        )}
        {isPlatformAdmin && (
          <List disablePadding>
            <NavRow
              item={{ key: 'console', label: 'Console', to: CONSOLE_PATH, icon: <InsightsOutlined />, match: (p) => p.startsWith(CONSOLE_PATH) }}
              active={pathname.startsWith(CONSOLE_PATH)}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </List>
        )}
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: collapsed ? 'column' : 'row', alignItems: 'center', gap: collapsed ? 0.75 : 1 }}>
          <Tooltip title={collapsed ? username : 'Account'} placement={collapsed ? 'right' : 'top'}>
            <Button
              aria-label={`Account menu for ${username}`}
              aria-haspopup="menu"
              onClick={(event) => setUserMenuAnchor(event.currentTarget)}
              sx={{
                flex: collapsed ? 'none' : 1,
                minWidth: 0,
                justifyContent: 'flex-start',
                gap: 1.25,
                px: collapsed ? 0.5 : 1,
                py: 0.5,
                color: 'var(--q-text, #1C1B19)',
                textTransform: 'none',
                borderRadius: 'var(--q-radius-lg, 10px)',
              }}
            >
              <Avatar sx={{ width: 30, height: 30, fontSize: 13, fontWeight: 600, bgcolor: 'secondary.main' }}>{initial}</Avatar>
              {!collapsed && (
                <Typography noWrap component="span" sx={{ fontWeight: 500, fontSize: 14 }}>
                  {username}
                </Typography>
              )}
            </Button>
          </Tooltip>
          <Tooltip title={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'} placement={collapsed ? 'right' : 'top'}>
            <IconButton aria-label={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'} onClick={toggleTheme} sx={{ width: 32, height: 32, color: 'var(--q-muted-2, #6B665F)' }}>
              {isDarkMode ? <Brightness7Outlined fontSize="small" /> : <Brightness4Outlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{username}</Typography>
          <Typography variant="caption" component="div" sx={{ color: 'var(--q-muted, #5E5A54)' }}>
            {accountName}
            {authState.currentMembership?.plan_name ? ` · ${authState.currentMembership.plan_name} plan` : ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { setUserMenuAnchor(null); go('/profile'); }}>
          <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        {authActions.canAccessSettings() && (
          <MenuItem onClick={() => { setUserMenuAnchor(null); onNavigate?.(); openSettings(); }}>
            <ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
        )}
        {onOpenFeedback && (
          <MenuItem onClick={() => { setUserMenuAnchor(null); onNavigate?.(); onOpenFeedback(); }}>
            <ListItemIcon><FeedbackOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Send feedback</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          component="a"
          {...WHATS_NEW_LINK_PROPS}
          aria-label="What's new (opens in a new tab)"
          onClick={() => setUserMenuAnchor(null)}
        >
          <ListItemIcon><NewReleasesOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>What&apos;s new</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
